import { type DistributionRange, noteRangePhrase, priorRangePhrase } from "./ranges";

export type AskTile = "channel" | "safety" | "creators" | "videos";

/** What to open the Ask drawer on: a question it submits, or prompts it offers. */
export type AskRequest = { question?: string; suggestions?: string[]; title?: string };

export const ASK_TITLE: Record<AskTile, string> = {
  channel: "Ask about channel performance",
  safety: "Ask about what's at risk",
  creators: "Ask about active creators",
  videos: "Ask about videos posted",
};

const TILE_PROMPTS: Record<AskTile, string[]> = {
  channel: [
    "How did total GMV split across Video, Product card and Live in the {period}, and how does each compare with the {prior_period}?",
    "Has the mix of total GMV across Video, Product card and Live shifted in the {period} compared with the {prior_period}?",
    "Which creators drove the most affiliate GMV in the {period}, and which of them dropped off compared with the {prior_period}?",
    "How has total GMV moved month over month over the last 6 months?",
  ],
  safety: [
    "What risk categories were found in videos analysed in the {period}, broken down by severity?",
    "Which creators had the most flagged videos in the {period}?",
    "How many flagged videos need to be reviewed, and how long have they been waiting?",
    "Are flagged videos in the {period} more likely to come from creators posting for the first time?",
  ],
  creators: [
    "How many creators who posted in the {period} generated any affiliate GMV, and what share is that?",
    "How many creators posting in the {period} were new versus returning, compared with the {prior_period}?",
    "What share of affiliate GMV in the {period} came from the top 1% of creators by GMV?",
    "Of the creators who posted in the {prior_period}, how many posted again in the {period}?",
  ],
  videos: [
    "What was GPM per video, in the {period} compared with the {prior_period}?",
    "Which products were featured in the most videos in the {period}?",
    "What were the median and mean videos per creator in the {period}?",
    "What share of videos posted in the {period} generated any affiliate GMV?",
  ],
};

// The window is written into the prompt text rather than sent alongside it: the request
// carries no range, so a prompt that leaves the period out is answered over Ask's own
// default window instead of the one the card is showing.
export const askPromptsFor = (tile: AskTile, range: DistributionRange, locale?: string): string[] => {
  const period = noteRangePhrase(range, locale);
  const prior = priorRangePhrase(range, locale);

  return TILE_PROMPTS[tile].map((prompt) => prompt.replaceAll("{period}", period).replaceAll("{prior_period}", prior));
};
